import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { features } from "../data/mock";

export const FeaturesSection = () => {
  const [headRef, headVisible] = useScrollAnimation();

  return (
    <section id="features" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-indigo-50/20 to-white" />
      {/* Animated decorative orbs */}
      <div className="absolute top-20 left-0 w-[400px] h-[400px] bg-indigo-100/30 rounded-full blur-[120px] pointer-events-none orb-float-2" />
      <div className="absolute bottom-0 right-10 w-[300px] h-[300px] bg-violet-100/25 rounded-full blur-[100px] pointer-events-none orb-float-1" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headRef}
          className={`text-center max-w-2xl mx-auto transition-all duration-700 ease-out ${
            headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <span className="text-xs font-semibold tracking-widest uppercase text-indigo-600">
            Features
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            One Platform. Every Step Covered.
          </h2>
          <p className="mt-4 text-lg text-slate-400 leading-relaxed font-body">
            From discovery to submission, IndiBids handles the heavy lifting so you can focus on winning.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="mt-16 grid lg:grid-cols-3 gap-6">
          {features.map((feature, i) => (
            <FeatureCard key={feature.id} feature={feature} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ feature, index }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.1 });
  const Icon = feature.icon;

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="group relative rounded-2xl p-8 h-full bg-white/70 backdrop-blur-md border border-white/60 shadow-sm card-glow-indigo overflow-hidden">
        {/* Animated glow overlay on hover */}
        <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-br from-indigo-100/50 via-transparent to-violet-50/30" />
        
        <div className="relative z-10">
          {/* Icon */}
          <div className="w-14 h-14 rounded-2xl bg-indigo-50/80 flex items-center justify-center mb-6 transition-all duration-500 group-hover:bg-indigo-100 group-hover:shadow-lg group-hover:shadow-indigo-200/50">
            <Icon size={24} className="text-indigo-600" />
          </div>

          {/* Content */}
          <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
          <p className="text-[0.9rem] text-slate-500 leading-relaxed mb-6 font-body">
            {feature.description}
          </p>

          {/* Highlight tags */}
          <div className="flex flex-wrap gap-2">
            {feature.highlights.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-50/60 text-indigo-600 border border-indigo-100/50"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-indigo-400 to-transparent scale-x-0 transition-transform duration-700 origin-center group-hover:scale-x-100" />
      </div>
    </div>
  );
};
